<script>
  import { config } from '../../config/client.js';
  import { afterUpdate, onMount, getContext } from 'svelte';
  import { loadUserSearchHistory } from "../services/searchService.js";
  import {
    currentSearchThreadId,
    followUpAnswer,
    followUpIsLoadingAnswer,
    followUpIsLoadingResults,
    followUpQueries,
    followUpQuery,
    followUpResults,
    followUpResultsVisible,
    followUpSearches,
    history,
    isLoadingAnswer,
    isLoadingQueryHeader,
    isLoadingResults,
    overallSummary,
    queryHeader,
    results,
    resultsVisible,
    user,
    userTier
  } from '../stores/stores';
  import { authService } from '../services/AuthService.js';
  import { get, writable } from 'svelte/store';
  import Search from './Search.svelte';
  import History from './History.svelte';
  import Results from './Results.svelte';
  import Answer from './Answer.svelte';
  import FollowUpSearch from './FollowUpSearch.svelte';
  import { t } from '../services/translation/translationService.js';
  import LanguageSelector from './LanguageSelector.svelte';
  import SignOutModal from './SignOutModal.svelte';
  import { fade } from 'svelte/transition';
  import { SvelteToast } from '@zerodevx/svelte-toast'
  import SearchTypes from "./SearchTypes.svelte";
  import { CheckUserUsage } from "../services/searchService.js";
  import { closeModal, Modals, openModal } from "svelte-modals";
  import ConfirmCancelModal from "./modals/ConfirmCancelModal.svelte";
  import StripePlansModal from "./modals/StripePlansModal.svelte";
  import MagicLinkModal from "./modals/MagicLinkModal.svelte";
  import SetupProfileModal from "./modals/SetupProfileModal.svelte";
  import { api } from "../services/api";   
    import { role } from 'better-auth/plugins/access';

  let userEmail = 'Guest';
  let rightContainer;

  let imageSrc = '/images/General/General.png';
  let imageAlt = 'General Ministry of Health';

  const isLatestFollowUpLoading = writable(false);

  let isAuthenticated = false;
  let isInitializing = false;
  let selectedType = 'basic';
  let showMenuMenu = false;
  let isLoggingOut = false;
  
  // Check if we're in development mode
  const isDevMode = process.env.NODE_ENV === 'developmentt';
  console.log('App.svelte: NODE_ENV =', process.env.NODE_ENV);
  console.log('App.svelte: isDevMode =', isDevMode);
  let initialTierOnLoad = null;

  console.log("Try to do something first part");
  function goToLogin() {
    openModal(MagicLinkModal, {
    // you can pass props if needed, e.g. prefilled email
    // email: prefillEmail
  });
  }

  onMount(async () => {
    console.log('App.svelte: onMount started, isLoggingOut =', isLoggingOut);
    if (isLoggingOut) return;

    try {
      console.log("Initializing authentication...");
      isInitializing = true;

      let currentUser;
      let userId=null;

      if (isDevMode) {
        console.log("Running in local dev mode, using mock authentication.");
        const devUser = {
          id: "dev-local-id",
          email: "dev-user@example.com",
          name: "Developer User",
        };
        user.set(devUser);
        userEmail = devUser.email;
        userId = devUser.id;
        await authService.ensureUserInDB({
          userId: devUser.id,
          email:devUser.email,
          firstName: "Developer",
          lastName: "User",
          role: "standard",
          subscriptionTier: "basic",
        });
        isAuthenticated = true;
        currentUser = devUser;
      } else {
        // 🔑 Better Auth: just check the session
        console.log("Start auth");
        const sessionUser = await authService.initialize(); // calls authClient.getSession()
        if (sessionUser) {
          //console.log("Authentication successful, user:", sessionUser.name);
          isAuthenticated = true;
          user.set(sessionUser);
          userEmail = sessionUser.name ?? "Guest";
          // Map your old localAccountId -> Better Auth user.id
          userId = sessionUser.userId ?? null;
          currentUser = sessionUser;
          if (userId) {
            await ensureProfileCompleted(userId);
          }
        } else {
          console.log("No session found. Show login (magic link) UI.");
          isAuthenticated = false;
          goToLogin();      // ⬅️ route to /login with an email form that calls authService.loginWithMagicLink(email)
          isInitializing = false;
          return;
        }
      }

      // ---- Your existing Stripe poll & usage checks (unchanged) ----
      initialTierOnLoad = get(userTier);
      console.log(`Initial tier on load (before explicit CheckUserUsage/polling): ${initialTierOnLoad}`);

      const urlParams = new URLSearchParams(window.location.search);
      const stripeStatus = urlParams.get('stripe_checkout_status');

      if (stripeStatus === 'success' && userId) {
        console.log('Stripe success redirect detected. Starting polling for tier update.');

        // Clean the URL
        let currentSearch = window.location.search;
        currentSearch = currentSearch.replace(/[\\?&]stripe_checkout_status=success/, '');
        if (currentSearch.startsWith('&')) currentSearch = '?' + currentSearch.substring(1);
        else if (currentSearch.length > 0 && !currentSearch.startsWith('?')) currentSearch = '?' + currentSearch;
        const newUrl = window.location.pathname + (currentSearch.length > 1 ? currentSearch : '') + window.location.hash;
        window.history.replaceState({}, document.title, newUrl);

        let attempts = 0;
        const maxAttempts = 5;
        const pollInterval = 2000;

        const pollForTierUpdate = async () => {
          attempts++;
          await CheckUserUsage(userId);
          const currentTier = get(userTier);

          console.log(`Polling attempt ${attempts}: Initial tier was '${initialTierOnLoad}', current tier is '${currentTier}'`);

          const hasChangedMeaningfully =
            currentTier !== initialTierOnLoad &&
            (initialTierOnLoad === 'basic' || currentTier !== 'basic');

          if (hasChangedMeaningfully) {
            console.log('User tier updated successfully after polling:', currentTier);
          } else if (attempts >= maxAttempts) {
            console.warn('Max polling attempts reached. Final check.');
            await CheckUserUsage(userId);
          } else {
            setTimeout(pollForTierUpdate, pollInterval);
          }
        };

        setTimeout(pollForTierUpdate, 500);
      } else if (userId) {
        console.log('Normal load or no Stripe redirect. Calling CheckUserUsage.');
        await CheckUserUsage(userId);
      } else if (!userId && !isDevMode) {
        console.warn("No userId available after auth init, CheckUserUsage cannot run.");
      } else if (!userId && isDevMode) {
        console.warn("No userId available in devMode onMount (should have been set from devUser).");
      }
      // -------------------------------------------------------------
    } catch (error) {
      console.error("Authentication or Initialization error in onMount:", error);
      if (error?.errorCode) console.error("Error code:", error.errorCode);
      if (error?.errorMessage) console.error("Error message:", error.errorMessage);
      isAuthenticated = false;
    } finally {
      isInitializing = false;
    }
  });

  function openSignOutModal() {
    openModal(ConfirmCancelModal, {
      message: $t('signOutText'),
      confirmButtonText: $t('signOutConfirmText'),
      onConfirm: handleSignOut,
      onCancel: ()=> {},
    });
  }
  function openStripeModal() {
    openModal(StripePlansModal, {
      message: $t('plansText'),
      confirmButtonText: $t('signOutConfirmText'),
      onCancel: ()=> {},
    });
  }

  function toggleMenu() {
    showMenuMenu = !showMenuMenu;
  }

  function handleCloseMenu() {
    if (showMenuMenu) {
      showMenuMenu = !showMenuMenu;
    }
  }

  function handleTypeSelected(event) {
    selectedType = event.detail.searchType;
  }


  async function handleSignOut() {
  isLoggingOut = true;

  try {
    // Call Better Auth logout
    await authService.logout();

    // Clear local state
    isAuthenticated = false;
    userEmail = "Guest";

    // Optionally reset search-related stores
    resetStores();
    history.set([]);

    // Close the ConfirmCancelModal
    closeModal();

    // Immediately show the Better Auth login popup again
    goToLogin();
  } catch (error) {
    console.error("Logout error:", error);
  } finally {
    isLoggingOut = false;
  }
}

  function updateVh() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  $: {
  if ($user) {
    userEmail =
      $user.username ||
      $user.name ||
      $user.email ||
      ([$user.FirstName, $user.LastName].filter(Boolean).join(" ") || "Guest");
  } else {
    userEmail = "Guest";
  }
}

  // Translation strings
  $: lastFollowUpQuery = $followUpQueries[$followUpQueries.length - 1];
  // Use the t function to translate strings
  $: newSearchText = $t('newSearch');
  $: historyText = $t('history');
  $: resultsText = $t('results');
  $: answerText = $t('answer');
  $: askMedicalQuestionText = $t('askMedicalQuestion');
  $: loadingResultsText = $t('loadingResults');
  $: generatingAnswerText = $t('generatingAnswer');

  function scrollToLatestQuery() {
    setTimeout(() => {
      const queryGroups = rightContainer.querySelectorAll('.query-group');
      if (queryGroups.length > 1) {
        const lastQueryIndex = queryGroups.length - 1;
        const secondLastQuery = queryGroups[lastQueryIndex - 1];
        const scrollTop = secondLastQuery.offsetTop + secondLastQuery.offsetHeight;
        rightContainer.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    }, 100);
  }

  function handleNewSearch() {
    handleCloseMenu();
    resetStores();
  }

  async function handleHistoryClick(item) {
    // Reset the state for follow-up queries and results
    followUpQueries.set([]);
    followUpResults.set([]);
    followUpAnswer.set([]);
    followUpResultsVisible.set([]);
    followUpIsLoadingResults.set([]);
    followUpIsLoadingAnswer.set([]);

    // Set current thread ID and load queries
    currentSearchThreadId.set(item.ThreadId);
    const threadQueries = item.Queries || [];

    if (threadQueries.length > 0) {
      queryHeader.set(threadQueries[0].query);
      results.set(threadQueries[0].results || []);
      overallSummary.set(threadQueries[0].answer || '');
      resultsVisible.set(true);

      // Set follow-up queries, excluding the first one
      if (threadQueries.length > 1) {
        const followUps = threadQueries.slice(1);
        followUpQueries.set(followUps.map(q => q.query));
        followUpResults.set(followUps.map(q => q.results || []));
        followUpAnswer.set(followUps.map(q => q.answer || ''));
        followUpResultsVisible.set(followUps.map(() => false));
        followUpIsLoadingResults.set(followUps.map(() => false));
        followUpIsLoadingAnswer.set(followUps.map(() => false));
      }
    } else {
      // Handle case with no queries
      queryHeader.set('Undefined Query');
      results.set([]);
      overallSummary.set('');
      resultsVisible.set(false);
    }

    // Additional logic to load and display the thread content
  }

  function getCountry() {
    var timezones = {
      "Europe/Athens": {
        bkg: "#013476",
        alt: "Hellenic Republic Ministry of Health",
        code: "GR"
      },
      "Europe/Bucharest": {
        bkg: "#1C62B7",
        alt: "Romanian Ministry of Health",
        code: "RO"
      },
      "Europe/Budapest": {
        bkg: "#EEEEEE",
        alt: "HUngarian Ministry of Health",
        code: "HU"
      },
      "Europe/Zagreb": {
        bkg: "#EEEEEE",
        alt: "Croatian Ministry of Health",
        code: "HR"
      },
      "Europe/Sofia": {
        bkg: "#2D3460",
        alt: "Bulgarian Ministry of Health",
        code: "BG"
      },

      "General": {
        bkg: "#EEEEEE",
        alt: "General Ministry of Health",
        code: "General"
      }

    };

    var timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (timezone === "" || !timezone || timezones[timezone] === "" || !timezones[timezone]) {
      timezone = "General";
    }
    const leftElement = document.getElementsByClassName('left')[0];

    // Change only the background-color, leave other styles intact
    //leftElement.style.backgroundColor = timezones[timezone].bkg;
    //document.getElementsByClassName('left-content')[0].style["background-color"]=timezones[timezone].bkg;
    //const image = document.getElementById('ministry-logo');
    //imageSrc = '/images/' + timezones[timezone].code + '/' + timezones[timezone].code + '.png';
    //imageAlt = timezones[timezone].alt;

  }

  function resetStores() {
    queryHeader.set('');
    results.set([]);
    overallSummary.set('');
    followUpResults.set([]);
    followUpAnswer.set([]);
    followUpQueries.set([]);
    resultsVisible.set(false);
    followUpResultsVisible.set([]);
    followUpQuery.set('');
    followUpIsLoadingResults.set([]);
    followUpIsLoadingAnswer.set([]);
    isLatestFollowUpLoading.set(false);
    isLoadingQueryHeader.set(false);
  }

  async function loadSearchItem(threadId) {
    console.log('Loading search thread ID:', threadId);
    queryHeader.set('');
    isLoadingResults.set(true);
    isLoadingAnswer.set(true);

    try {
      const response = await fetch(`${config.serverUrl}/api/search/thread/${threadId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const completeSearchData = await response.json();
      // console.log('Received search data:', completeSearchData);

      if (completeSearchData.Queries && completeSearchData.Queries.length > 0) {
        const initialQuery = completeSearchData.Queries[0];
        queryHeader.set(initialQuery.query);
        results.set(initialQuery.results || []);
        overallSummary.set(initialQuery.answer || '');
        resultsVisible.set(true);

        // Handle follow-ups
        const followUpQueriesArray = completeSearchData.Queries.slice(1);
        followUpQueries.set(followUpQueriesArray.map(q => q.query));
        followUpResults.set(followUpQueriesArray.map(q => q.results || []));
        followUpAnswer.set(followUpQueriesArray.map(q => q.answer || ''));
        followUpResultsVisible.set(followUpQueriesArray.map(() => false));
        followUpIsLoadingResults.set(followUpQueriesArray.map(() => false));
        followUpIsLoadingAnswer.set(followUpQueriesArray.map(() => false));
      } else {
        console.error('No queries found in the search data');
      }

    } catch (error) {
      console.error('Error fetching complete search data:', error);
      results.set([]);
      overallSummary.set('Error loading search data');
    } finally {
      isLoadingResults.set(false);
      isLoadingAnswer.set(false);
    }
  }

  $: if (lastFollowUpQuery) {
    scrollToLatestQuery();
  }

  afterUpdate(() => {
    if ($followUpQueries.length > 0) {
      scrollToLatestQuery();
    }
  });

  $: {
    if ($queryHeader) {
      const currentThread = get(history).find(item => item.query === $queryHeader);
      if (currentThread) {
        localStorage.setItem('currentSearchThread', JSON.stringify(currentThread));
      }
    } else {
      localStorage.removeItem('currentSearchThread');
    }
  }

  $: if (!$isLoadingAnswer && $overallSummary) {
    resultsVisible.set(false);
  }

  $: {
    $followUpAnswer.forEach((answer, index) => {
      if (answer && !$followUpIsLoadingAnswer[index]) {
        followUpResultsVisible.update(visibilities => {
          const newVisibilities = [...visibilities];
          newVisibilities[index] = false;
          return newVisibilities;
        });
      }
    });
  }

  $: {
    if ($followUpQueries.length > 0) {
      followUpSearches.set($followUpQueries.map((query, index) => ({
        query,
        results: $followUpResults[index],
        answer: $followUpAnswer[index],
        resultsVisible: $followUpResultsVisible[index]
      })));
    } else {
      followUpSearches.set([]);
    }
  }

  $: placeholder = $t('askMedicalQuestion');

  $: if ($user && ($user.userId || $user.id)) {
    (async () => {
      try {
        const id = $user.userId ?? $user.id;   // support both shapes during transition
        const loadedHistory = await loadUserSearchHistory(id);
        console.log('Loaded history:', loadedHistory);
        history.set(loadedHistory);
      } catch (error) {
        console.error('Failed to load user history:', error);
      }
    })();
  }

  onMount(() => {
    getCountry();
    // updateVh();
    window.addEventListener('resize', updateVh);
    document.addEventListener('DOMContentLoaded', updateVh);
    window.addEventListener('orientationchange', updateVh);
  });

  function toggleResults() {
    resultsVisible.update(visible => !visible);
  }

  function toggleFollowUpResults(index) {
    followUpResultsVisible.update(visibleArray => {
      const newArray = [...visibleArray];
      newArray[index] = !newArray[index];
      return newArray;
    });
  }

async function ensureProfileCompleted(userId) {
  try {
    const resp = await api.get(`/api/users/${userId}`);
    const profile = resp.data;

    if (!profile) return;
console.log("Name:"+profile.firstName+" and id"+userId);
    const missing =
      !profile.firstName ||
      !profile.lastName 

    if (missing) {
      openModal(SetupProfileModal, {
        userId,
        existing: profile,
        onSaved: (updated) => {
          // update the Svelte user store with fresh profile info
          user.update((u) => ({
            ...u,
            firstName: updated.firstName,
            lastName: updated.lastName,
            country_code: updated.country_code,
            city: updated.city,
            hospital: updated.hospital,
            role: updated.role,
            specialty: updated.speciality,
          }));
        },
      });
    }
  } catch (e) {
    console.error("Failed to check/complete profile:", e);
  }
}
</script>

<style>
  :global(html, body) {
    margin: 0;
    padding: 0;
    height: 100%;
    overflow: hidden;
  }

  #toast-container {
    visibility: hidden;
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10000;
    background-color: rgba(255, 0, 0, 0);
    color: white;
    border-radius: 8px;
    padding: 15px;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.3);
  }

  #toast-container > div {
    visibility: visible;
    background-color: rgba(255, 0, 0, 0.7);
    color: white;
  }

  #app {
    display: flex;
    height: 100vh;
    width: 100vw;
  }

  .container {
    display: flex;
    width: 100%;
    height: 100%;
    position: relative;
  }

  .mobile-menu-trigger {
    position: absolute;
    display: none;
    top: 0;
    left: 0;
    padding: 10px;
    z-index: 5;
    cursor: pointer;
    color: black;
    font-size: 2rem;
  }

  @media (max-width: 1000px) {
    .mobile-menu-trigger {
      display: inline-block;
    }

    .search-bar-container {
      width: 90%;
    }

    .left {
      position: absolute !important;
      left: -100%;
      z-index: 4;
      /*height: calc(100vh - 55px);*/
    }

    .left.open {
      position: absolute !important;
      width: 67vw;
      left: 0;
      height: calc(var(--vh, 1vh) * 100);
    }

    .left.open + .mobile-menu-trigger {
      color: white;
    }

    .content-wrapper {
      text-align: left;
      padding: 24px 16px 24px 40px;
      width: initial;
    }

    .content-wrapper .content {
      width: initial;
    }

    .follow-up-search-bar {
      left: calc(17% + 66.25% / 2) !important;
    }
  }

  .left {
    /*flex: 0.75;*/
    background-color: #003476;
    border-right: 1px solid #333;
    color: #ffffff;
    display: flex;
    flex-direction: column;
    /*height: 100vh;*/
    position: relative;
    overflow-y: auto;
    transition: left 0.3s ease-in-out;
    justify-content: space-between;
    width: 28%;
    height: 100%;
  }

  .left-content {
    /*flex-grow: 1;*/
    width: 100%;
    overflow-y: auto;
    /* Remove padding-bottom that was pushing content up */
    /* padding-bottom: 60px; */
    display: flex;
    flex-direction: column;
    align-items: center;
    scrollbar-width: none;
    -ms-overflow-style: none;
    max-height: calc(100% - 60px);
    height: calc(100% - 60px);
  }

  .left-content::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }

  .logo-button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    outline: none;
  }

  .logo-button:focus-visible {
    outline: 2px solid #003476;
  }

  .logo-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding-top: 8px;
  }

  .ministry-logo {
    width: auto;
    max-width: 110px;
    margin-bottom: 0;
    padding-top: 8px;
  }

  .new-search-button {
    background-color: #002357;
    color: #ffffff;
    border: none;
    padding: 8px 0;
    cursor: pointer;
    width: 100%;
    text-align: center;
    font-size: 1.2em;
    margin-top: 0;
    overflow: auto;
  }

  .history-container {
    flex-grow: 1;
    width: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    scrollbar-width: none;
    -ms-overflow-style: none;
    height: calc(100% - 60px);
  }

  .history-container::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }

  .right {
    /*flex: 3.25;*/
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    color: #333333;
    /*height: 100vh;*/
    width: 100%;
    justify-content: center;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  .scrollable-content {
    /*flex-grow: 1;*/
    overflow-y: auto;
    /*padding-bottom: 80px;*/
  }

  .content-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 100%;
  }

  .content {
    width: 50%;
    max-width: 800px;
    word-wrap: break-word;
  }

  .query-group {
    margin-bottom: 40px;
    padding-bottom: 20px;
  }

  .query-group:last-child {
    border-bottom: none;
  }

  .query-group h2 {
    margin-bottom: 5px;
    font-size: 1.8em;
  }

  .no-results-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 100%;
    height: 100vh;
  }

  .search-bar-container {
    min-width: 60%;
    display: flex;
    align-items: start;
    justify-content: center;
    position: relative;
  }

  .language-selector-container {
    margin-top: 10px;
    margin-left: 20px;
    flex-shrink: 0;
  }

  .follow-up-search-bar {
    position: fixed;
    bottom: 0px;
    left: calc(25% + 66.25% / 2);
    transform: translateX(-50%);
    width: 66.25%;
    padding: 0px 0;
    background-color: transparent;
    z-index: 3;
    transition: opacity 0.2s ease-in-out;
  }

  .spinner {
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top: 4px solid #fff;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .user-profile-bar {
    /*position: absolute;*/
    bottom: 61px;
    left: 0;
    right: 0;
    height: 60px;
    background-color: #333;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 15px;
    border-top: 1px solid #444;
    background-color: #002357;
  }

  .user-profile {
    display: flex;
    align-items: center;
    padding-right: 6px;
  }

  .profile-picture {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #555;
    margin-right: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #fff;
  }
  .profile-plan-wrapper {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
  }
  .profile-plan-wrapper .cta {
    cursor: pointer;
  }

  .username {
    color: #fff;
    font-size: 14px;
  }

  .settings-icon {
    color: #fff;
    font-size: 40px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -8px;
  }

  .history-title {
    color: #ffffff;
    margin: 0;
    padding: 5px 0;
    text-align: center;
    width: 100%;
  }

  .loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
  }

  .spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #003476;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .prometheus-logo {
    width: 300px;
    height: auto;
    margin-bottom: 20px;
  }

  .backdrop {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.50);
    z-index: 4;
    /*pointer-events: none;*/
  }
</style>

<!--<SignOutModal-->
<!--  on:cancel={closeSignOutModal}-->
<!--  on:signout={handleSignOut}-->
<!--  show={showSignOutModal}-->
<!--/>-->

<div id="app">
  <div class="container">
    <SvelteToast/>
    <div id="toast-container">
      <div></div>
    </div>
    <div class="left" class:open="{ showMenuMenu }">
      <div class="left-content">
        <div class="logo-container">
          <button
            aria-label="New Search"
            class="logo-button"
            on:click={handleNewSearch}
          >
            <img
              alt={imageAlt}
              class="ministry-logo"
              src={imageSrc}
            >
          </button>
          <button class="new-search-button" on:click={handleNewSearch}>{newSearchText}</button>
        </div>
        <h3 class="history-title">{historyText}</h3>
        <div class="history-container" on:click={handleCloseMenu} on:keydown={(e) => handleCloseMenu()}>
          <History {handleHistoryClick}/>
        </div>
      </div>

      <!-- User profile bar -->
      <div class="user-profile-bar">
        <div class="user-profile">
          <div class="profile-picture">
            {userEmail.charAt(0).toUpperCase()}
          </div>
          <div>
            <span class="username">{userEmail}</span>
            <div class="profile-plan-wrapper">
              <span style="text-transform: capitalize">Plan: <i>{$userTier}</i> </span>
              <div class="cta" 
                on:click={openStripeModal}
                on:keydown={(e) => e.key === 'Enter' && openStripeModal()}
                role="button"
                tabindex="0">Change</div>
            </div>
          </div>
        </div>
        <div
          aria-label="Open settings"
          class="settings-icon"
          on:click={openSignOutModal}
          on:keydown={(e) => e.key === 'Enter' && openSignOutModal()}
          role="button"
          tabindex="0"
        >⚙
        </div>
      </div>
    </div>
    <div class="mobile-menu-trigger" on:click={toggleMenu} on:keydown={(e) => toggleMenu()}>≣</div>
    <div class="right" on:click={handleCloseMenu} on:keydown={(e) => handleCloseMenu()}>
      <div bind:this={rightContainer} class="scrollable-content">
        {#if !$queryHeader}
          <div class="no-results-container">
            <img src="/images/PrometheusLogo.jpg" alt="Prometheus Research Engine" class="prometheus-logo">
            <div class="search-bar-container">
              <div style="width: 100%; display: flex; flex-direction: column; align-items: flex-start; flex-grow: 1;">
                <Search placeholder={$t('askMedicalQuestion')} searchType="{selectedType}"/>
                <SearchTypes on:typeSelected={handleTypeSelected}></SearchTypes>
              </div>
              <div class="language-selector-container">
                <LanguageSelector/>
              </div>
            </div>
          </div>
        {:else}
          <div class="content-wrapper">
            <div class="content">
              <div class="query-group">
                <h2>{$queryHeader}</h2>
                {#if $isLoadingQueryHeader}
                  <div class="loading-spinner">
                    <span class="spinner"></span>
                    <p>{loadingResultsText}</p>
                  </div>
                {:else if $isLoadingResults}
                  <div class="loading-spinner">
                    <span class="spinner"></span>
                    <p>{loadingResultsText}</p>
                  </div>
                {:else}
                  {#if $isLoadingAnswer}
                    <Results
                      results={$results}
                      visible={$resultsVisible}
                      on:toggle={toggleResults}
                      resultsText={resultsText}
                    />
                    <div class="loading-spinner" transition:fade={{ duration: 100 }}>
                      <span class="spinner"></span>
                      <p>{generatingAnswerText}</p>
                    </div>
                  {:else}
                    <Results
                      results={$results}
                      visible={$resultsVisible}
                      on:toggle={toggleResults}
                      resultsText={resultsText}
                    />
                    <div transition:fade={{ duration: 100 }}>
                      <Answer answer={$overallSummary} answerText={answerText} isLoading={false}
                              results={$results}/>
                    </div>
                  {/if}
                {/if}
              </div>

              <!-- Follow-up queries section -->
              {#each $followUpQueries as followUpQuery, index}
                <div class="query-group">
                  <h2>{followUpQuery}</h2>
                  {#if $followUpIsLoadingResults[index]}
                    <div class="loading-spinner">
                      <span class="spinner"></span>
                      <p>{loadingResultsText}</p>
                    </div>
                  {:else}
                    {#if $followUpIsLoadingAnswer[index]}
                      <Results
                        results={$followUpResults[index] || []}
                        visible={$followUpResultsVisible[index]}
                        on:toggle={() => toggleFollowUpResults(index)}
                        resultsText={resultsText}
                      />
                      <div class="loading-spinner" transition:fade={{ duration: 100 }}>
                        <span class="spinner"></span>
                        <p>{generatingAnswerText}</p>
                      </div>
                    {:else}
                      <Results
                        results={$followUpResults[index] || []}
                        visible={$followUpResultsVisible[index]}
                        on:toggle={() => toggleFollowUpResults(index)}
                        resultsText={resultsText}
                      />
                      <div transition:fade={{ duration: 100 }}>
                        <Answer answer={$followUpAnswer[index]} answerText={answerText}
                                isLoading={false} results={$followUpResults[index]}/>
                      </div>
                    {/if}
                  {/if}
                </div>
              {/each}
            </div>
          </div>
        {/if}
      </div>
      {#if $queryHeader}
        <div class="follow-up-search-bar">
          <FollowUpSearch {placeholder}/>
        </div>
      {/if}
    </div>
  </div>
</div>
<Modals>
  <div
    class="backdrop"
    on:click={closeModal}
    on:keydown={(e) => e.key === 'Escape' && closeModal()}
    role="button"
    tabindex="0"
    slot="backdrop"
  />
</Modals>